// Filter panel - owns filter state, syncs URL hash, fires "filter:change" events
import { qs, qsa, on, delegate, lockScroll } from '../lib/dom.js';
import { num } from '../lib/format.js';
import { AP_DATA } from '../data/listings.js';
import { getMultiselect, createMultiselect, initMultiselects } from './multiselect.js';
import { t } from '../lib/i18n.js';

// Brand + body options come from AP_DATA (WP-localized). Model lists are
// fetched per brand from /wp-json/autoplaza/v1/models and cached.
const MODELS_CACHE = new Map();

// Every OTHER active filter, serialised the way the REST endpoint reads it.
//
// The model list has to be narrowed by them, not just by the brand: with Sedan
// already picked, BMW must offer 320d and not X3/X5, which the filter excludes
// and which produced an empty catalog when tapped (client report). Brand and
// model are left out on purpose - brand goes in the URL separately, and a facet
// never filters itself.
//
// Keys match atpl_rest_filter_input() exactly, so what the client narrows by is
// what the server narrows by. `collection` is deliberately absent: the catalog
// filter builder has no such facet, so sending it would only split the cache.
function modelNarrowParams() {
  const p = new URLSearchParams();
  const multi = (key, set) => { if (set && set.size) p.set(key, Array.from(set).join(',')); };
  multi('body', STATE.bodies);
  multi('fuel', STATE.fuels);
  multi('trans', STATE.trans);
  multi('drive', STATE.drives);
  multi('color', STATE.colors);
  multi('options', STATE.options);
  multi('engine', STATE.engine);
  multi('city', STATE.cities);
  multi('seats', STATE.seats);
  multi('availability', STATE.availability);
  multi('origin', STATE.origin);
  const range = (key, v) => { if (v != null) p.set(key, String(v)); };
  range('priceMin', STATE.priceMin);
  range('priceMax', STATE.priceMax);
  range('rateMin', STATE.rateMin);
  range('rateMax', STATE.rateMax);
  range('powerMin', STATE.powerMin);
  range('powerMax', STATE.powerMax);
  range('yearMin', STATE.yearMin);
  range('yearMax', STATE.yearMax);
  range('kmMin', STATE.kmMin);
  range('kmMax', STATE.kmMax);
  range('consumptionMax', STATE.consumptionMax);
  if (STATE.mode && STATE.mode !== 'all') p.set('mode', STATE.mode);
  p.sort(); // stable key whatever order the filters were toggled in
  return p;
}

// Cache entries are per brand AND per narrowing context - the same brand yields
// a different model list once another filter changes, so the context has to be
// part of the key or a stale list would be replayed.
function modelCacheKey(brand) {
  return `${brand}|${modelNarrowParams().toString()}`;
}

async function fetchModelsForBrand(brand) {
  const key = modelCacheKey(brand);
  if (MODELS_CACHE.has(key)) return MODELS_CACHE.get(key);
  if (!AP_DATA.apiBase) {
    MODELS_CACHE.set(key, []);
    return [];
  }
  try {
    // Send the page language explicitly - the REST endpoint is on the neutral
    // /wp-json/ base and otherwise has to guess the language from the Referer,
    // which is dropped by privacy settings/proxies and silently returns RO.
    const qs = modelNarrowParams();
    qs.set('brand', brand);
    if (AP_DATA.lang) qs.set('lang', AP_DATA.lang);
    const res = await fetch(`${AP_DATA.apiBase}models?${qs}`, {
      headers: AP_DATA.nonce ? { 'X-WP-Nonce': AP_DATA.nonce } : {},
      credentials: 'same-origin',
    });
    const list = res.ok ? await res.json() : [];
    MODELS_CACHE.set(key, list);
    return list;
  } catch {
    MODELS_CACHE.set(key, []);
    return [];
  }
}

function allBrandNames() {
  return (AP_DATA.brands || [])
    .map((b) => b.name)
    .filter(Boolean)
    .sort((a, b) => String(a).localeCompare(String(b)));
}

function bodyOptions() {
  return (AP_DATA.bodies || [])
    .filter((b) => b.key && b.key !== 'all')
    .map((b) => ({ value: b.key, label: b.name }));
}

const PRICE_MAX_CAP = 100000;
const RATE_MAX_CAP = 2000;   // €/month - slider ceiling for the monthly-rate filter
const KM_MAX_CAP = 300000;

const STATE = {
  q: '',             // free-text search (matches title/brand)
  mode: 'all',          // all | used | new
  brands: new Set(),
  models: new Set(),
  priceMin: null,
  priceMax: null,
  rateMin: null,           // monthly leasing rate, €/month (server maps to price)
  rateMax: null,
  yearMin: null,
  yearMax: null,
  bodies: new Set(),
  // The four facets below store TERM SLUGS (lowercased, hyphen-separated),
  // not display names - the server-rendered filter panel uses slugs as
  // checkbox/pill values so they line up across languages.
  fuels: new Set(),
  trans: new Set(),
  colors: new Set(),
  drives: new Set(),
  collection: new Set(),    // collection term slugs
  options: new Set(),      // Opțiuni (Features) term slugs
  engine: new Set(),      // Engine Size term slugs
  seats: new Set(),      // seat counts (strings)
  availability: new Set(),  // availability state slugs (disponibil|in_drum|la_comanda)
  origin: new Set(),      // origin_country values (raw)
  powerMin: null,
  powerMax: null,
  cities: new Set(),        // dealer_location term slugs (multi-select, mirrors trans)
  kmMin: null,
  kmMax: null,
  consumptionMax: null,     // max combined fuel consumption l/100km (fuel-calc CTA)
};

// The active catalog sort key (e.g. 'price-asc'). Owned by the catalog page
// but kept HERE so the single canonical URL writer (buildCatalogURL) can emit
// `?sort=…` alongside the filter path. 'relevance' = default → no param.
// Without this, every filter tick's syncHash() would strip `?sort=` from the URL.
let sortParam = 'relevance';

// Current pagination page. Lives here (not just in catalog.js) so the canonical
// URL writer can keep `/page/N/` in the address bar - otherwise a direct load of
// /catalog/bmw/page/4/ gets its page silently stripped by syncHash() on init,
// which reads as "the page bounced back to page 1".
let pagedParam = 1;

// Let the catalog page push the current sort into the canonical URL writer and
// rewrite the address bar in one place. Returns nothing - call syncHash() so
// filters + sort share the SAME replaceState writer (no competing history).
export function setSortParam(sort) {
  sortParam = sort || 'relevance';
  syncHash();
}

// Same single-writer contract for the page number (used by catalog.js when the
// user paginates or changes filters/sort, which resets to page 1).
export function setPagedParam(page) {
  pagedParam = Math.max(1, parseInt(page, 10) || 1);
  syncHash();
}

// The page parsed out of the URL path on load, so catalog.js can adopt it as
// its initial currentPage (keeping the grid + pagination in step with the URL).
export function getPagedParam() {
  return pagedParam;
}

export function getFilterState() {
  return {
    q: STATE.q,
    mode: STATE.mode,
    brands: Array.from(STATE.brands),
    models: Array.from(STATE.models),
    priceMin: STATE.priceMin,
    priceMax: STATE.priceMax,
    rateMin: STATE.rateMin,
    rateMax: STATE.rateMax,
    yearMin: STATE.yearMin,
    yearMax: STATE.yearMax,
    bodies: Array.from(STATE.bodies),
    fuels: Array.from(STATE.fuels),
    trans: Array.from(STATE.trans),
    colors: Array.from(STATE.colors),
    drives: Array.from(STATE.drives),
    collection: Array.from(STATE.collection),
    options: Array.from(STATE.options),
    engine: Array.from(STATE.engine),
    seats: Array.from(STATE.seats),
    availability: Array.from(STATE.availability),
    origin: Array.from(STATE.origin),
    powerMin: STATE.powerMin,
    powerMax: STATE.powerMax,
    cities: Array.from(STATE.cities),
    kmMin: STATE.kmMin,
    kmMax: STATE.kmMax,
    consumptionMax: STATE.consumptionMax,
  };
}

// Case-insensitive helpers - defensive matching so a stale link or
// hand-typed URL still hits the right listings.
function setHasCI(set, value) {
  if (set.has(value)) return true;
  const lc = String(value).toLowerCase();
  for (const v of set) if (String(v).toLowerCase() === lc) return true;
  return false;
}

export function applyFilters(listings) {
  const qLow = STATE.q ? STATE.q.toLowerCase().trim() : '';
  const modelTokens = STATE.models.size ? Array.from(STATE.models).map((m) => m.toLowerCase()) : null;
  return listings.filter((l) => {
    if (qLow) {
      const hay = `${l.title} ${l.brand} ${l.body} ${l.fuel}`.toLowerCase();
      if (!hay.includes(qLow)) return false;
    }
    if (STATE.mode === 'new' && l.year < 2023) return false;
    if (STATE.mode === 'used' && l.year >= 2023) return false;
    if (STATE.brands.size > 0 && !setHasCI(STATE.brands, l.brand)) return false;
    if (modelTokens) {
      const t = l.title ? l.title.toLowerCase() : '';
      if (!modelTokens.some((m) => t.includes(m))) return false;
    }
    if (STATE.priceMin != null && l.price < STATE.priceMin) return false;
    if (STATE.priceMax != null && l.price > STATE.priceMax) return false;
    if (STATE.rateMin != null && l.monthly != null && l.monthly < STATE.rateMin) return false;
    if (STATE.rateMax != null && l.monthly != null && l.monthly > STATE.rateMax) return false;
    if (STATE.yearMin != null && l.year < STATE.yearMin) return false;
    if (STATE.yearMax != null && l.year > STATE.yearMax) return false;
    if (STATE.bodies.size > 0 && !setHasCI(STATE.bodies, l.body)) return false;
    if (STATE.fuels.size > 0 && !setHasCI(STATE.fuels, l.fuel)) return false;
    if (STATE.trans.size > 0 && !setHasCI(STATE.trans, l.trans)) return false;
    // color is mocked - listings don't have it so we ignore unless user adds it
    if (STATE.kmMin != null && l.miles < STATE.kmMin) return false;
    if (STATE.kmMax != null && l.miles > STATE.kmMax) return false;
    return true;
  });
}

// Resolve a brand STATE value to its display name. STATE holds display
// names (panel selections) OR canonical slugs (URL adoption); resolve
// against AP_DATA.brands either way. When the brand isn't in the payload
// (e.g. zero stock in this language), title-case the slug instead of
// showing it raw - used by the applied chips AND the synced catalog H1.
function brandDisplay(v) {
  const hit = (AP_DATA.brands || []).find((b) =>
    b.slug === v || (b.name || '').toLowerCase() === String(v).toLowerCase());
  if (hit) return hit.name;
  return String(v).replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function fireChange() {
  document.dispatchEvent(new CustomEvent('filter:change', { detail: getFilterState() }));
  syncHash();
  renderApplied();
  syncCatalogTitle();
  // Browser-tab <title> follows the active filters, but ONLY from the SECOND fire
  // onward: the first fire is the load-time reconcile, and leaving the title alone
  // there keeps the server's SEO <title> on a no-interaction (Googlebot) render.
  // Cleared filters restore the original.
  if (CATALOG_TITLE_ORIG === null) CATALOG_TITLE_ORIG = document.title;
  if (FILTER_FIRED_ONCE) {
    if (LAST_ACTIVE_FILTERS > 0) {
      const h1el = qs('.ap-cat-title');
      const heading = h1el ? (h1el.textContent || '').replace(/\.\s*$/, '').trim() : '';
      if (heading) document.title = heading;
    } else {
      document.title = CATALOG_TITLE_ORIG;
    }
  }
  FILTER_FIRED_ONCE = true;
  // Facet counts are no longer fetched here: the catalog's /listings call
  // piggybacks them (?facets=1) and pushes them into applyFacetCounts(),
  // so a filter change costs one round trip instead of two.
}

// Keep the catalog H1 in sync with the active selection - mirrors the
// server-side atpl_catalog_heading() so an in-page filter change updates the
// heading without a reload. Format: "Mașini {brand model body fuel …}
// [în {location}]" (no mode word once a term filter is active). Falls back to
// the server's default heading (data-default-title, e.g. "Toate mașinile"),
// mode-suffixed in syncCatalogTitle(), when no term filter is selected.
let DEFAULT_CAT_TITLE = null;
// SEO H1 override for the CURRENT selection, pushed from the /listings?facets=1
// response (server side: atpl_listings_override_heading). Non-empty only for a
// pure single-brand / brand+model selection that has a stored override; '' for
// every faceted / multi-brand / mode / base selection. When set it WINS over the
// client-computed template heading in syncCatalogTitle(); on first load it equals
// the already server-rendered H1, so there's no flicker.
let SERVER_HEADING = '';
let SERVER_HEADING_SEEDED = false; // one-shot: seed from the server-rendered H1 attr on first sync
let CATALOG_TITLE_ORIG = null;     // server-rendered <title>, captured on first fire
let FILTER_FIRED_ONCE = false;     // skip the load-time fire so we never override the server <title> on Googlebot's no-interaction render
let LAST_ACTIVE_FILTERS = 0;       // # of active filter chips (set by renderApplied)

// Called by the catalog after each /listings?facets=1 response so the H1 tracks
// the server override as brands are toggled in-page.
export function setServerHeading(heading) {
  SERVER_HEADING = typeof heading === 'string' ? heading : '';
  SERVER_HEADING_SEEDED = true; // a real /listings response is authoritative
  syncCatalogTitle();
}

function catalogTitleText() {
  const SEP = ' / ';        // values within a facet / brand
  const GROUP_SEP = ', ';   // between brand groups
  const brands = Array.from(STATE.brands).filter(Boolean).map(brandDisplay); // display names
  const selected = new Set(STATE.models);                    // model slugs

  // Pair each selected model under its own brand → "Audi A4, Fiat 500".
  const groups = [];
  const claimed = new Set();
  brands.forEach((brand) => {
    const mine = modelOptionsForSync([brand]).filter((o) => selected.has(o.value));
    mine.forEach((o) => claimed.add(o.value));
    groups.push(mine.length ? `${brand} ${mine.map((o) => o.label).join(SEP)}` : brand);
  });
  // With no brand selected, list any chosen models on their own. (When a brand
  // IS selected, unclaimed models are just awaiting their cache - skip so we
  // never split a model off from its brand.)
  if (!brands.length) {
    Array.from(selected).map(modelLabel).filter(Boolean).forEach((m) => groups.push(m));
  }

  const subject = groups.filter(Boolean).join(GROUP_SEP);

  const body = Array.from(STATE.bodies).map(bodyLabel).filter(Boolean);
  const fuel = Array.from(STATE.fuels).map((f) => facetLabel('car_fuel', f) || f).filter(Boolean);
  const trans = Array.from(STATE.trans).map((t) => facetLabel('Transmission', t) || t).filter(Boolean);
  const drives = Array.from(STATE.drives).map((d) => facetLabel('Drive', d) || d).filter(Boolean);
  const colors = Array.from(STATE.colors).map((c) => facetLabel('Colors', c) || c).filter(Boolean);
  const cities = Array.from(STATE.cities).map((c) => facetLabel('dealer_location', c) || c).filter(Boolean);
  const city = cities.join(SEP);

  const parts = [
    subject,
    body.join(SEP),
    fuel.join(SEP),
    trans.join(SEP),
    drives.join(SEP),
    colors.join(SEP),
  ].filter(Boolean);

  if (!parts.length && !city) return '';

  let title = t('cat_subject', 'Mașini');
  if (parts.length) title += ' ' + parts.join(' ');
  if (city) title += t('cat_in', ' în ') + city;
  return title;
}

function syncCatalogTitle() {
  const el = qs('.ap-cat-title');
  if (!el) return;
  // Seed the server override from the page-load H1 attribute ONCE, before the
  // first (load-time) sync. Otherwise the initial fireChange would replace the
  // server-rendered SEO H1 with the client template. A later /listings response
  // (setServerHeading) supersedes this.
  if (!SERVER_HEADING_SEEDED) {
    SERVER_HEADING_SEEDED = true;
    const seed = el.getAttribute('data-server-heading');
    if (seed) SERVER_HEADING = seed;
  }
  if (DEFAULT_CAT_TITLE === null) {
    DEFAULT_CAT_TITLE = el.getAttribute('data-default-title') || (el.textContent || '').replace(/\.\s*$/, '').trim();
  }

  let text;
  if (SERVER_HEADING) {
    // Server override wins: a pure single-brand / brand+model selection with a
    // stored SEO H1. Mirrors the server-rendered page-load H1 exactly (matches
    // on first load, appears/disappears as brands are toggled).
    text = SERVER_HEADING;
  } else {
    // Template heading (multi-brand / faceted / mode / base selection). With no
    // active term filter the heading is a full mode-specific phrase (used →
    // "Automobile cu parcurs", new → "Automobile noi", all → the bare base
    // title "Toate mașinile") - mirrors archive-cars.php. These are FULL
    // translatable strings via t(), NOT the base title + a raw RO word, so the
    // RU catalog reads naturally ("Новые автомобили") instead of leaving "noi"
    // untranslated next to "Все автомобили". As soon as any term filter is
    // active, catalogTitleText() wins and carries no mode word.
    text = catalogTitleText();
    if (!text) {
      if (STATE.mode === 'used') text = t('cat_title_used', 'Automobile cu parcurs');
      else if (STATE.mode === 'new') text = t('cat_title_new', 'Automobile noi');
      else text = DEFAULT_CAT_TITLE;
    }
  }
  // Rebuild as a text node + the red accent dot (avoids innerHTML injection).
  el.textContent = text;
  const dot = document.createElement('span');
  dot.className = 'ap-cat-title__dot';
  dot.textContent = '.';
  el.appendChild(dot);
}

// Read a localized display label out of the server-rendered filter panel
// (any `[data-facet-row][data-slug]` matching the slug in the given facet
// group). Returns empty string if nothing matches - applied-chips falls
// back to the slug in that case.
function facetLabel(taxonomy, slug) {
  const group = qs(`[data-facet-group="${taxonomy}"] [data-slug="${CSS.escape(String(slug))}"]`);
  if (!group) return '';

  // <option> text is "Name (12)" - drop the trailing count.
  if (group.tagName === 'OPTION') {
    return (group.textContent || '').replace(/\s+/g, ' ').replace(/\s*\(\d[\d.,]*\)\s*$/, '').trim();
  }

  // Prefer an explicit label span. Some rows (e.g. transmission pills) keep the
  // label as a bare text node next to a count badge - in that case clone the
  // row, strip the count, and read what's left so we never include the number.
  const label = qs('span:not(.ap-checkbox__box):not(.ap-checkbox__count):not(.ap-chip__count)', group);
  if (label) {
    return (label.textContent || '').replace(/\s+/g, ' ').trim();
  }
  const clone = group.cloneNode(true);
  clone.querySelectorAll('.ap-chip__count, .ap-checkbox__count, [data-role="facet-count"]').forEach((el) => el.remove());
  return (clone.textContent || '').replace(/\s+/g, ' ').trim();
}

// Slug-ify a value for URL segments. Matches WP's sanitize_title() and the
// homepage search-bar's `atplSlug` helper.
function urlSlug(s) {
  return String(s == null ? '' : s)
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function brandSlug(name) {
  const hit = (AP_DATA.brands || []).find((b) => b.name === name || b.slug === name);
  return hit && hit.slug ? hit.slug : urlSlug(name);
}

function bodyKeyOf(val) {
  const hit = (AP_DATA.bodies || []).find((b) => b.key === val || b.name === val);
  return hit ? hit.key : urlSlug(val);
}

// Reverse of bodyKeyOf: resolve a stored body key (e.g. "suv") to its display
// label (e.g. "SUV") for the applied-filter chips. Body is a multiselect, not a
// facet-group, so facetLabel() can't find it - read the name from AP_DATA.
function bodyLabel(val) {
  const hit = (AP_DATA.bodies || []).find((b) => b.key === val || b.name === val || atplSlug(b.key) === atplSlug(val));
  return hit && hit.name ? hit.name : val;
}

// Resolve a model slug (the URL/state value) to its display name using the
// already-loaded model options for the selected brands. Falls back to a
// prettified slug until that brand's models have been fetched.
function modelLabel(slug) {
  const hit = modelOptionsForSync(Array.from(STATE.brands)).find((o) => o.value === slug);
  if (hit) return hit.label;
  return String(slug).replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// Drop selected models that no longer belong to a selected brand. With no
// brands left, clear every model - a model can't stand on its own. When brands
// remain we only prune against their *cached* model lists (skip if not fetched
// yet, so we don't drop valid-but-unloaded models; sync reconciles later).
function pruneOrphanModels() {
  if (STATE.models.size === 0) return;
  if (STATE.brands.size === 0) {
    STATE.models.clear();
    return;
  }
  const valid = new Set(modelOptionsForSync(Array.from(STATE.brands)).map((o) => o.value));
  if (valid.size) {
    Array.from(STATE.models).forEach((m) => { if (!valid.has(m)) STATE.models.delete(m); });
  }
}

// Build the canonical /catalog/{filter-slugs}/ pretty URL from STATE.
// Trailing slash always included. Free-text search and range filters go in
// a query string after the path so the page stays unambiguously canonical.
function buildCatalogURL(pageOverride) {
  const catalog = (AP_DATA.catalog || '/catalog/').replace(/\/+$/, '/') || '/catalog/';
  const segments = [];

  // Brand (no prefix - bare slugs, joined with -or-).
  if (STATE.brands.size) {
    const slugs = Array.from(STATE.brands).map(brandSlug);
    segments.push(slugs.join('-or-'));
  }
  // Prefixed filters.
  const addPrefixed = (prefix, values, slugFn) => {
    if (!values || !values.size) return;
    const slugs = Array.from(values).map(slugFn);
    segments.push(prefix + '-' + slugs.join('-or-'));
  };
  addPrefixed('model', STATE.models, urlSlug);
  addPrefixed('body', STATE.bodies, bodyKeyOf);
  addPrefixed('fuel', STATE.fuels, urlSlug);
  addPrefixed('transmission', STATE.trans, urlSlug);
  addPrefixed('drive', STATE.drives, urlSlug);
  addPrefixed('color', STATE.colors, urlSlug);
  addPrefixed('collection', STATE.collection, urlSlug);
  addPrefixed('option', STATE.options, urlSlug);
  addPrefixed('dealer', STATE.cities, urlSlug);

  // Range / free-text + meta filters go in the query string.
  const qs = new URLSearchParams();
  if (STATE.q) qs.set('s', STATE.q);
  if (STATE.mode !== 'all') qs.set('mode', STATE.mode);
  // Sort rides in the query string so it survives every filter change (the
  // catalog page keeps sortParam current via setSortParam()). Default sort
  // ('relevance', server's date-DESC) emits no param so the URL stays clean.
  if (sortParam && sortParam !== 'relevance') qs.set('sort', sortParam);
  if (STATE.priceMin != null) qs.set('priceMin', STATE.priceMin);
  if (STATE.priceMax != null) qs.set('priceMax', STATE.priceMax);
  if (STATE.rateMin != null) qs.set('rateMin', STATE.rateMin);
  if (STATE.rateMax != null) qs.set('rateMax', STATE.rateMax);
  if (STATE.powerMin != null) qs.set('powerMin', STATE.powerMin);
  if (STATE.powerMax != null) qs.set('powerMax', STATE.powerMax);
  if (STATE.yearMin != null) qs.set('yearMin', STATE.yearMin);
  if (STATE.yearMax != null) qs.set('yearMax', STATE.yearMax);
  if (STATE.kmMin != null) qs.set('kmMin', STATE.kmMin);
  if (STATE.kmMax != null) qs.set('kmMax', STATE.kmMax);
  if (STATE.consumptionMax != null) qs.set('consumptionMax', STATE.consumptionMax);
  if (STATE.engine.size) qs.set('engine', Array.from(STATE.engine).join(','));
  if (STATE.seats.size) qs.set('seats', Array.from(STATE.seats).join(','));
  if (STATE.availability.size) qs.set('availability', Array.from(STATE.availability).join(','));
  if (STATE.origin.size) qs.set('origin', Array.from(STATE.origin).join(','));
  // city/dealer now goes in the path as `dealer-{slug}` (see addPrefixed
  // above) so we don't also put it in the query string - that would duplicate.

  let path = catalog + (segments.length ? segments.join('/') + '/' : '');
  // Preserve pagination in the canonical URL (e.g. /catalog/bmw/page/3/).
  // `pageOverride` lets callers build the URL for an arbitrary page (used by the
  // pagination anchors) instead of the current `pagedParam`.
  const pg = pageOverride != null ? Math.max(1, parseInt(pageOverride, 10) || 1) : pagedParam;
  if (pg > 1) {
    path += 'page/' + pg + '/';
  }
  const query = qs.toString();
  return path + (query ? '?' + query : '');
}

// Canonical catalog URL for a given page, reflecting the CURRENT filter/sort
// state - the pagination component uses this for its anchor hrefs so the links
// are real, crawlable URLs (matching the server's `/page/N/` pagination).
export function catalogUrlForPage(page) {
  return buildCatalogURL(page);
}

// Replace the URL bar with the canonical pretty URL - no hash anywhere.
// Uses replaceState so we don't pollute browser history on every filter tick.
function syncHash() {
  const newUrl = buildCatalogURL();
  const current = window.location.pathname + window.location.search + window.location.hash;
  if (newUrl !== current) {
    history.replaceState(null, '', newUrl);
  }
}

const splitList = (s) => String(s).split(',').map((v) => v.trim()).filter(Boolean);

function readFromHash() {
  const hash = window.location.hash.replace(/^#/, '');
  if (!hash) return;
  hash.split('&').forEach((part) => {
    const [k, v] = part.split('=');
    if (!k || v == null) return;
    const dec = decodeURIComponent(v);
    if (k === 'q' || k === 's') STATE.q = dec;
    else if (k === 'mode') STATE.mode = dec;
    else if (k === 'brand') splitList(dec).forEach((b) => STATE.brands.add(canonicalizeBrand(b)));
    else if (k === 'model') splitList(dec).forEach((m) => STATE.models.add(m));
    else if (k === 'priceMin') STATE.priceMin = +dec;
    else if (k === 'priceMax') STATE.priceMax = +dec;
    else if (k === 'rateMin') STATE.rateMin = +dec;
    else if (k === 'rateMax') STATE.rateMax = +dec;
    // legacy: ?budget=X behaves like priceMax
    else if (k === 'budget') STATE.priceMax = +dec;
    else if (k === 'yearMin') STATE.yearMin = +dec;
    else if (k === 'yearMax') STATE.yearMax = +dec;
    else if (k === 'body') dec.split(',').forEach((b) => STATE.bodies.add(canonicalizeBody(b)));
    else if (k === 'fuel') dec.split(',').forEach((f) => STATE.fuels.add(atplSlug(f)));
    else if (k === 'trans') dec.split(',').forEach((t) => STATE.trans.add(atplSlug(t)));
    else if (k === 'color') dec.split(',').forEach((c) => STATE.colors.add(atplSlug(c)));
    else if (k === 'drive') dec.split(',').forEach((d) => STATE.drives.add(atplSlug(d)));
    else if (k === 'collection') dec.split(',').forEach((c) => STATE.collection.add(atplSlug(c)));
    else if (k === 'options') dec.split(',').forEach((o) => STATE.options.add(atplSlug(o)));
    else if (k === 'engine') dec.split(',').forEach((en) => STATE.engine.add(atplSlug(en)));
    else if (k === 'seats') dec.split(',').forEach((s) => STATE.seats.add(String(s).replace(/[^0-9]/g, '')));
    else if (k === 'availability') dec.split(',').forEach((a) => { const v = String(a).toLowerCase().replace(/[^a-z_]/g, ''); if (v) STATE.availability.add(v); });
    else if (k === 'origin') dec.split(',').forEach((o) => STATE.origin.add(o));
    else if (k === 'powerMin') STATE.powerMin = +dec;
    else if (k === 'powerMax') STATE.powerMax = +dec;
    else if (k === 'city') dec.split(',').forEach((c) => STATE.cities.add(atplSlug(c)));
    else if (k === 'kmMin') STATE.kmMin = +dec;
    else if (k === 'kmMax') STATE.kmMax = +dec;
    else if (k === 'consumptionMax') STATE.consumptionMax = +dec;
  });
}

// Slug helper - mirror WP's sanitize_title() closely enough for matching.
// Lowercases, strips diacritics, replaces non-alphanumerics with `-`.
function atplSlug(s) {
  return String(s == null ? '' : s)
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// Map any case/slug variation of a URL value to the canonical value used
// in the dataset.
function canonicalizeBrand(raw) {
  const rawSlug = atplSlug(raw);
  const hit = (AP_DATA.brands || []).find((b) =>
    atplSlug(b.name) === rawSlug || atplSlug(b.slug || '') === rawSlug
  );
  return hit ? hit.name : raw;
}
function canonicalizeBody(raw) {
  const rawSlug = atplSlug(raw);
  const hit = (AP_DATA.bodies || []).find((b) => atplSlug(b.key) === rawSlug);
  return hit ? hit.key : String(raw).toLowerCase();
}
function canonicalizeFuel(raw) {
  const rawSlug = atplSlug(raw);
  const hit = (AP_DATA.fuels || []).find((f) => atplSlug(f) === rawSlug);
  return hit || raw;
}

// Read filter values out of the URL path - e.g. /catalog/renault/model-juke/.
//
// Mirrors the PHP routing in `inc/filter-routing.php`:
//   - first non-prefixed segment = manufacturer slug
//   - any segment starting with `model-` / `body-` / `fuel-` / etc. = prefixed filter
//   - values can be joined with `-or-` for multi-value
//   - trailing `/page/N/` becomes paged (ignored here, server handles paging)
function readFromPath() {
  // Strip the catalog base path derived from the server-provided catalog URL.
  // AP_DATA.catalog = site_url('/[ru/]catalog/'), so it already includes any
  // WordPress subdirectory AND the language prefix. Hardcoding 'catalog/' here
  // broke when WP is installed in a subdirectory (e.g. /autoplaza/): the subdir
  // segment leaked through and was parsed as a bogus manufacturer filter.
  let basePath = '/catalog/';
  try {
    basePath = new URL(AP_DATA.catalog || '/catalog/', window.location.origin).pathname;
  } catch (e) { /* keep the default */ }
  basePath = basePath.replace(/\/+$/, '');                 // tolerate trailing slash

  const here = (window.location.pathname || '').replace(/\/+$/, '');
  if (basePath && here.indexOf(basePath) !== 0) return;    // not under the catalog base
  const path = here.slice(basePath.length).replace(/^\/+/, '');
  if (!path) return;

  let segments = path.split('/').filter(Boolean);

  // Peel a trailing /page/N - it's not a filter segment, but capture it as the
  // initial page so the canonical URL writer preserves it (no silent reset to 1).
  if (segments.length >= 2 && segments[segments.length - 2] === 'page' && /^\d+$/.test(segments[segments.length - 1])) {
    pagedParam = Math.max(1, parseInt(segments[segments.length - 1], 10) || 1);
    segments = segments.slice(0, -2);
  }
  if (!segments.length) return;

  const prefixMap = {
    'model': { setName: 'models', canon: (v) => v },
    'body': { setName: 'bodies', canon: canonicalizeBody },
    'transmission': { setName: 'trans', canon: atplSlug },
    'fuel': { setName: 'fuels', canon: atplSlug },
    'drive': { setName: 'drives', canon: atplSlug },
    'color': { setName: 'colors', canon: atplSlug },
    'collection': { setName: 'collection', canon: atplSlug },
    'option': { setName: 'options', canon: atplSlug },
    'dealer': { setName: 'cities', canon: atplSlug },
  };

  const splitOr = (raw) => raw.split('-or-').map((v) => v.trim()).filter(Boolean);

  const applyPrefixed = (prefix, rawValue) => {
    const cfg = prefixMap[prefix];
    if (!cfg || cfg.skip) return;
    const values = splitOr(rawValue);
    values.forEach((v) => STATE[cfg.setName].add(cfg.canon(v)));
  };

  const tryParsePrefixed = (segment) => {
    for (const prefix of Object.keys(prefixMap)) {
      if (segment.indexOf(prefix + '-') === 0) {
        return { prefix, value: segment.substring(prefix.length + 1) };
      }
    }
    return null;
  };

  // First segment: manufacturer slug OR a prefixed filter.
  const first = segments[0];
  const firstPrefixed = tryParsePrefixed(first);
  if (firstPrefixed) {
    applyPrefixed(firstPrefixed.prefix, firstPrefixed.value);
  } else {
    splitOr(first).forEach((slug) => STATE.brands.add(canonicalizeBrand(slug)));
  }

  // Remaining segments: each is a prefixed filter.
  for (let i = 1; i < segments.length; i++) {
    const parsed = tryParsePrefixed(segments[i]);
    if (parsed) applyPrefixed(parsed.prefix, parsed.value);
  }
}

// Read filter values from the URL query string (e.g. ?brand=BMW&body=suv).
// Used for first-load deep-linking; the in-page hash takes over from here.
// Accepts both `?brand=BMW,Audi` (comma list) and `?brand=BMW&brand=Audi`
// (repeated params - what the homepage form's hidden inputs produce).
function readFromQuery() {
  const sp = new URLSearchParams(window.location.search);
  // Free-text search. The header search dialog submits `?s=…` (WP-native
  // search key) and buildCatalogURL() also emits `?s=…`; `?q=…` is kept as a
  // legacy alias. Reading only `q` here silently dropped every header search,
  // so the catalog re-painted the full, unfiltered set.
  if (sp.get('s')) STATE.q = sp.get('s');
  else if (sp.get('q')) STATE.q = sp.get('q');
  if (sp.get('mode')) STATE.mode = sp.get('mode');
  // Seed the sort so the first syncHash() (fired by initFilterPanel before the
  // catalog page wires its sort handler) preserves a `?sort=…` deep-link.
  if (sp.get('sort')) sortParam = sp.get('sort');
  if (sp.get('city')) splitList(sp.get('city')).forEach((c) => STATE.cities.add(atplSlug(c)));

  if (sp.get('priceMin') != null) STATE.priceMin = +sp.get('priceMin');
  if (sp.get('priceMax') != null) STATE.priceMax = +sp.get('priceMax');
  // Legacy `?price=` and `?budget=` both behave like priceMax now
  if (sp.get('price')) STATE.priceMax = +sp.get('price');
  if (sp.get('budget') != null) STATE.priceMax = +sp.get('budget');

  if (sp.get('rateMin') != null) STATE.rateMin = +sp.get('rateMin');
  if (sp.get('rateMax') != null) STATE.rateMax = +sp.get('rateMax');

  if (sp.get('yearMin') != null) STATE.yearMin = +sp.get('yearMin');
  if (sp.get('yearMax') != null) STATE.yearMax = +sp.get('yearMax');
  if (sp.get('kmMin') != null) STATE.kmMin = +sp.get('kmMin');
  if (sp.get('kmMax') != null) STATE.kmMax = +sp.get('kmMax');
  // Combined-consumption ceiling from the car page fuel-calc CTA (?consumptionMax=7).
  if (sp.get('consumptionMax') != null) STATE.consumptionMax = +sp.get('consumptionMax');
  if (sp.get('powerMin') != null) STATE.powerMin = +sp.get('powerMin');
  if (sp.get('powerMax') != null) STATE.powerMax = +sp.get('powerMax');

  // Multi-value: each repeated param can also be a comma list
  sp.getAll('brand').flatMap(splitList).forEach((b) => STATE.brands.add(canonicalizeBrand(b)));
  sp.getAll('model').flatMap(splitList).forEach((m) => STATE.models.add(m));
  if (sp.get('body')) splitList(sp.get('body')).forEach((b) => STATE.bodies.add(canonicalizeBody(b)));
  if (sp.get('fuel')) splitList(sp.get('fuel')).forEach((f) => STATE.fuels.add(atplSlug(f)));
  if (sp.get('trans')) splitList(sp.get('trans')).forEach((t) => STATE.trans.add(atplSlug(t)));
  if (sp.get('color')) splitList(sp.get('color')).forEach((c) => STATE.colors.add(atplSlug(c)));
  if (sp.get('drive')) splitList(sp.get('drive')).forEach((d) => STATE.drives.add(atplSlug(d)));
  if (sp.get('collection')) splitList(sp.get('collection')).forEach((c) => STATE.collection.add(atplSlug(c)));
  if (sp.get('options')) splitList(sp.get('options')).forEach((o) => STATE.options.add(atplSlug(o)));
  if (sp.get('engine')) splitList(sp.get('engine')).forEach((en) => STATE.engine.add(atplSlug(en)));
  if (sp.get('seats')) splitList(sp.get('seats')).forEach((s) => STATE.seats.add(String(s).replace(/[^0-9]/g, '')));
  if (sp.get('availability')) splitList(sp.get('availability')).forEach((a) => { const v = String(a).toLowerCase().replace(/[^a-z_]/g, ''); if (v) STATE.availability.add(v); });
  if (sp.get('origin')) splitList(sp.get('origin')).forEach((o) => STATE.origin.add(o));
}

// Fuel + transmission + drive + color + location options are server-rendered
// in template-parts/catalog/filter-panel.php (so labels come out in the
// current Polylang language and initial counts reflect the real DB). JS
// only needs to keep checkbox state in sync with STATE - see syncPanelToState.
function renderFuelChecks() { /* server-rendered now */ }

// Write facet counts into every `[data-role="facet-count"]` element in both
// the inline panel and the drawer clone. The counts arrive piggybacked on
// the catalog's /listings response (catalog.js calls this) - there is no
// separate facet fetch anymore.
export function applyFacetCounts(counts) {
  if (!counts) return;

  // Update every facet group across all panels (inline + drawer clone).
  qsa('[data-facet-group]').forEach((group) => {
    const tax = group.dataset.facetGroup;
    // No data for this taxonomy in the response → leave the group untouched
    // (show everything) rather than guessing 0 for all of it.
    const hasData = !!counts && Object.prototype.hasOwnProperty.call(counts, tax);
    const taxCounts = hasData ? (counts[tax] || {}) : null;
    const rows = qsa('[data-facet-row]', group);

    const avail = rows.map((row) => {
      const n = taxCounts ? Number(taxCounts[row.dataset.slug] || 0) : 0;
      const badge = qs('[data-role="facet-count"]', row);
      if (badge && taxCounts) badge.textContent = n;
      const selected = row.tagName === 'OPTION'
        ? row.selected
        : (!!qs('input:checked', row) || row.classList.contains('is-active'));
      // "available" = has matches, is selected, or we simply have no data.
      return { row, available: !taxCounts || n > 0 || selected };
    });

    // Only hide empties when at least one option IS available. If the whole
    // group is empty (no data, or the attribute isn't on any car), show it all
    // so the filter never silently disappears.
    const anyAvailable = avail.some((a) => a.available);
    avail.forEach(({ row, available }) => {
      const hide = !!taxCounts && anyAvailable && !available;
      if (row.tagName === 'OPTION') {
        row.hidden = hide && !!row.value;
        row.disabled = hide && !!row.value;
      } else {
        row.classList.toggle('is-empty', hide);
      }
    });
    group.hidden = false; // never collapse - a vanished filter reads as broken
  });
}

// Resolve model options for a set of brands using the REST cache. Returns
// a flat list for one brand, a grouped list for multiple brands.
async function modelOptionsFor(brands) {
  if (brands.length === 0) return [];
  const lists = await Promise.all(brands.map(fetchModelsForBrand));
  const out = [];
  if (brands.length === 1) {
    lists[0].forEach((m) => out.push({ value: m.slug, label: m.name }));
    return out;
  }
  brands.forEach((b, i) => {
    lists[i].forEach((m) => out.push({ group: b, value: m.slug, label: m.name }));
  });
  return out;
}

// Synchronous variant - only uses already-cached models. Safe to call from
// state-syncing code paths where we don't want to await a network call.
function modelOptionsForSync(brands) {
  if (brands.length === 0) return [];
  const out = [];
  if (brands.length === 1) {
    (MODELS_CACHE.get(modelCacheKey(brands[0])) || []).forEach((m) => out.push({ value: m.slug, label: m.name }));
    return out;
  }
  brands.forEach((b) => {
    (MODELS_CACHE.get(modelCacheKey(b)) || []).forEach((m) => out.push({ group: b, value: m.slug, label: m.name }));
  });
  return out;
}

// In-flight token for the async model-options fetch, one per panel.
//
// Ticking two brands fast and then unticking one fires three overlapping syncs,
// each awaiting its own set of /models calls. Whichever resolves LAST used to
// win, and that is routinely the stale one: the two-brand sync has to wait for
// the brand that was just dropped, while the one-brand sync that superseded it
// answers from cache immediately. The visible result is the bug Stefan hit -
// one brand selected, both brands' models still listed.
//
// Keyed by panel, not a single module counter, because the desktop panel and the
// mobile drawer sync independently; a shared token would let one panel's sync
// cancel the other's and leave it with an empty model list.
const MODEL_SYNC_TOKEN = new WeakMap();

async function syncBrandModelMultis(panel) {
  const brandRoot = qs('[data-filter="brand"][data-component="multiselect"]', panel);
  const modelRoot = qs('[data-filter="model"][data-component="multiselect"]', panel);
  if (!brandRoot || !modelRoot) return;
  const brand = getMultiselect(brandRoot) || createMultiselect(brandRoot);
  const model = getMultiselect(modelRoot) || createMultiselect(modelRoot);

  brand.setOptions(allBrandNames().map((b) => ({ value: b, label: b })));
  brand.setSelected(Array.from(STATE.brands));

  const token = (MODEL_SYNC_TOKEN.get(panel) || 0) + 1;
  MODEL_SYNC_TOKEN.set(panel, token);

  const brandsArr = Array.from(STATE.brands);
  model.setEnabled(brandsArr.length > 0);
  model.setPlaceholder(brandsArr.length > 0 ? t('loading', 'Se încarcă…') : t('pick_brand_first', 'Alege marca întâi'));
  const opts = await modelOptionsFor(brandsArr);
  // A newer sync has taken over while we were waiting - its brand set is the
  // current one, so drop everything below rather than paint a stale list.
  if (MODEL_SYNC_TOKEN.get(panel) !== token) return;
  model.setOptions(opts);
  model.setPlaceholder(brandsArr.length > 0 ? (opts.length ? t('any_model', 'Orice model') : t('no_model', 'Niciun model disponibil')) : t('pick_brand_first', 'Alege marca întâi'));
  model.setSelected(Array.from(STATE.models));
  // Models load async - re-render the applied chips + heading now that slugs
  // can resolve to their display names (e.g. "q5" → "Q5").
  renderApplied();
  syncCatalogTitle();
}

function syncBodyMulti(panel) {
  const bodyRoot = qs('[data-filter="body"][data-component="multiselect"]', panel);
  if (!bodyRoot) return;
  const body = getMultiselect(bodyRoot) || createMultiselect(bodyRoot);
  body.setOptions(bodyOptions());
  body.setSelected(Array.from(STATE.bodies));
}

function syncModeTabs() {
  qsa('[data-mode]').forEach((b) => {
    const active = b.dataset.mode === STATE.mode;
    b.classList.toggle('is-active', active);
    if (b.hasAttribute('aria-selected')) b.setAttribute('aria-selected', String(active));
  });
}

// Reflect a dual-thumb range's STATE into the DOM: both thumb positions, the
// numeric value labels, aria-valuenow, and the fill drawn between the thumbs.
// `min`/`max` are the STATE values (null = unbounded → shown as 0 / cap).
function syncDualRange(panel, cfg) {
  const cap = cfg.cap;
  const lo = cfg.min ?? 0;
  const hi = cfg.max ?? cap;
  const minEl = qs(`[data-role="${cfg.minSlider}"]`, panel);
  if (minEl) { minEl.value = lo; minEl.setAttribute('aria-valuenow', lo); }
  const maxEl = qs(`[data-role="${cfg.maxSlider}"]`, panel);
  if (maxEl) { maxEl.value = hi; maxEl.setAttribute('aria-valuenow', hi); }
  const minLabel = qs(`[data-role="${cfg.minVal}"]`, panel);
  if (minLabel) minLabel.textContent = num(lo);
  const maxLabel = qs(`[data-role="${cfg.maxVal}"]`, panel);
  if (maxLabel) maxLabel.textContent = num(hi);
  const fillEl = qs(`[data-role="${cfg.fill}"]`, panel);
  if (fillEl) {
    fillEl.style.left = `${cap ? (lo / cap) * 100 : 0}%`;
    fillEl.style.right = `${cap ? 100 - (hi / cap) * 100 : 0}%`;
  }
}

function syncPanelToState(panel) {
  syncBrandModelMultis(panel);
  syncBodyMulti(panel);

  // Price inputs + dual-thumb slider (min thumb ↔ priceMin, max thumb ↔ priceMax)
  const priceMinIn = qs('[data-filter="priceMin"]', panel);
  if (priceMinIn) priceMinIn.value = STATE.priceMin ?? '';
  const priceMaxIn = qs('[data-filter="priceMax"]', panel);
  if (priceMaxIn) priceMaxIn.value = STATE.priceMax ?? '';
  syncDualRange(panel, {
    minSlider: 'price-min-slider', maxSlider: 'price-max-slider',
    minVal: 'price-min-value', maxVal: 'price-max-value',
    fill: 'price-fill', cap: PRICE_MAX_CAP,
    min: STATE.priceMin, max: STATE.priceMax,
  });

  // Monthly-rate inputs + max-rate slider (mirror of price)
  const rateMinIn = qs('[data-filter="rateMin"]', panel);
  if (rateMinIn) rateMinIn.value = STATE.rateMin ?? '';
  const rateMaxIn = qs('[data-filter="rateMax"]', panel);
  if (rateMaxIn) rateMaxIn.value = STATE.rateMax ?? '';
  const rateMaxSlider = qs('[data-role="rate-max-slider"]', panel);
  if (rateMaxSlider) rateMaxSlider.value = STATE.rateMax ?? RATE_MAX_CAP;
  const rateMaxVal = qs('[data-role="rate-max-value"]', panel);
  if (rateMaxVal) rateMaxVal.textContent = num(STATE.rateMax ?? RATE_MAX_CAP);

  // Year inputs
  const yMin = qs('[data-filter="yearMin"]', panel);
  if (yMin) yMin.value = STATE.yearMin ?? '';
  const yMax = qs('[data-filter="yearMax"]', panel);
  if (yMax) yMax.value = STATE.yearMax ?? '';

  // Pill groups (trans + city/location - body is a multiselect now)
  qsa('button[data-filter="trans"]', panel).forEach((btn) => {
    btn.classList.toggle('is-active', STATE.trans.has(btn.dataset.value));
  });
  qsa('button[data-filter="city"]', panel).forEach((btn) => {
    btn.classList.toggle('is-active', STATE.cities.has(btn.dataset.value));
  });

  // Checkbox groups (fuel, color, drive)
  qsa('input[type="checkbox"][data-filter="fuel"]', panel).forEach((cb) => {
    cb.checked = STATE.fuels.has(cb.value);
  });
  qsa('input[type="checkbox"][data-filter="color"]', panel).forEach((cb) => {
    cb.checked = STATE.colors.has(cb.value);
  });
  qsa('input[type="checkbox"][data-filter="drive"]', panel).forEach((cb) => {
    cb.checked = STATE.drives.has(cb.value);
  });
  qsa('input[type="checkbox"][data-filter="collection"]', panel).forEach((cb) => { cb.checked = STATE.collection.has(cb.value); });
  qsa('input[type="checkbox"][data-filter="options"]', panel).forEach((cb) => { cb.checked = STATE.options.has(cb.value); });
  qsa('input[type="checkbox"][data-filter="engine"]', panel).forEach((cb) => { cb.checked = STATE.engine.has(cb.value); });
  qsa('input[type="checkbox"][data-filter="seats"]', panel).forEach((cb) => { cb.checked = STATE.seats.has(cb.value); });
  qsa('input[type="checkbox"][data-filter="availability"]', panel).forEach((cb) => { cb.checked = STATE.availability.has(cb.value); });
  qsa('input[type="checkbox"][data-filter="origin"]', panel).forEach((cb) => { cb.checked = STATE.origin.has(cb.value); });

  // Cai putere inputs
  const powerMinIn = qs('[data-filter="powerMin"]', panel);
  if (powerMinIn) powerMinIn.value = STATE.powerMin ?? '';
  const powerMaxIn = qs('[data-filter="powerMax"]', panel);
  if (powerMaxIn) powerMaxIn.value = STATE.powerMax ?? '';

  // Km inputs + dual-thumb slider (min thumb ↔ kmMin, max thumb ↔ kmMax)
  const kmMinIn = qs('[data-filter="kmMin"]', panel);
  if (kmMinIn) kmMinIn.value = STATE.kmMin ?? '';
  const kmMaxIn = qs('[data-filter="kmMax"]', panel);
  if (kmMaxIn) kmMaxIn.value = STATE.kmMax ?? '';
  syncDualRange(panel, {
    minSlider: 'km-min-slider', maxSlider: 'km-max-slider',
    minVal: 'km-min-value', maxVal: 'km-max-value',
    fill: 'km-fill', cap: KM_MAX_CAP,
    min: STATE.kmMin, max: STATE.kmMax,
  });
}

function bindPanel(panel) {
  if (!panel || panel.dataset.bound === '1') return;
  panel.dataset.bound = '1';

  // Brand + model multiselects fire `multiselect:change` (bubbles to panel)
  on(panel, 'multiselect:change', (e) => {
    const { name, values } = e.detail || {};
    if (name === 'brand') {
      STATE.brands = new Set(values);
      // Picking a brand/model supersedes a leftover free-text search: the search
      // matches title/brand text, so combining it with an explicit brand facet
      // (e.g. `/catalog/bmw/?s=Audi+Q7` after searching then filtering) yields
      // zero results. Drop the search so the structural filter wins.
      STATE.q = '';
      // Removing a brand removes its models too (and all models if no brand
      // is left selected).
      pruneOrphanModels();
      syncAllPanels();
      fireChange();
    } else if (name === 'model') {
      STATE.models = new Set(values);
      STATE.q = ''; // see brand branch: an explicit model supersedes the search
      syncAllPanels();
      fireChange();
    } else if (name === 'body') {
      STATE.bodies = new Set(values);
      syncAllPanels();
      fireChange();
    }
  });

  // Price inputs + dual-thumb slider (min thumb ↔ priceMin, max thumb ↔ priceMax)
  delegate(panel, 'input', '[data-filter="priceMin"]', (e, input) => {
    STATE.priceMin = input.value === '' ? null : +input.value;
    syncAllPanels();
    fireChange();
  });
  delegate(panel, 'input', '[data-filter="priceMax"]', (e, input) => {
    STATE.priceMax = input.value === '' ? null : +input.value;
    syncAllPanels();
    fireChange();
  });
  delegate(panel, 'input', '[data-role="price-min-slider"]', (e, sl) => {
    let v = +sl.value;
    const hi = STATE.priceMax ?? PRICE_MAX_CAP;
    if (v > hi) v = hi;                   // clamp: min ≤ max
    STATE.priceMin = v <= 0 ? null : v;   // 0 → no lower bound
    syncAllPanels();
    fireChange();
  });
  delegate(panel, 'input', '[data-role="price-max-slider"]', (e, sl) => {
    let v = +sl.value;
    const lo = STATE.priceMin ?? 0;
    if (v < lo) v = lo;                   // clamp: max ≥ min
    STATE.priceMax = v >= PRICE_MAX_CAP ? null : v;
    syncAllPanels();
    fireChange();
  });

  // Monthly-rate inputs + slider (all share STATE.rateMax)
  delegate(panel, 'input', '[data-filter="rateMin"]', (e, input) => {
    STATE.rateMin = input.value === '' ? null : +input.value;
    fireChange();
  });
  delegate(panel, 'input', '[data-filter="rateMax"]', (e, input) => {
    STATE.rateMax = input.value === '' ? null : +input.value;
    syncAllPanels();
    fireChange();
  });
  delegate(panel, 'input', '[data-role="rate-max-slider"]', (e, sl) => {
    const v = +sl.value;
    STATE.rateMax = v >= RATE_MAX_CAP ? null : v;
    syncAllPanels();
    fireChange();
  });

  // Year inputs
  delegate(panel, 'input', '[data-filter="yearMin"]', (e, input) => {
    STATE.yearMin = input.value === '' ? null : +input.value;
    fireChange();
  });
  delegate(panel, 'input', '[data-filter="yearMax"]', (e, input) => {
    STATE.yearMax = input.value === '' ? null : +input.value;
    fireChange();
  });

  // Pill toggles (trans + city/location - body is now a multiselect)
  delegate(panel, 'click', 'button[data-filter="trans"]', (e, btn) => {
    const v = btn.dataset.value;
    if (STATE.trans.has(v)) STATE.trans.delete(v); else STATE.trans.add(v);
    syncAllPanels();
    fireChange();
  });
  delegate(panel, 'click', 'button[data-filter="city"]', (e, btn) => {
    const v = btn.dataset.value;
    if (STATE.cities.has(v)) STATE.cities.delete(v); else STATE.cities.add(v);
    syncAllPanels();
    fireChange();
  });

  // Checkboxes (fuel, color, drive)
  delegate(panel, 'change', 'input[type="checkbox"][data-filter="fuel"]', (e, cb) => {
    if (cb.checked) STATE.fuels.add(cb.value); else STATE.fuels.delete(cb.value);
    syncAllPanels();
    fireChange();
  });
  delegate(panel, 'change', 'input[type="checkbox"][data-filter="color"]', (e, cb) => {
    if (cb.checked) STATE.colors.add(cb.value); else STATE.colors.delete(cb.value);
    syncAllPanels();
    fireChange();
  });
  delegate(panel, 'change', 'input[type="checkbox"][data-filter="drive"]', (e, cb) => {
    if (cb.checked) STATE.drives.add(cb.value); else STATE.drives.delete(cb.value);
    syncAllPanels();
    fireChange();
  });
  delegate(panel, 'change', 'input[type="checkbox"][data-filter="collection"]', (e, cb) => {
    if (cb.checked) STATE.collection.add(cb.value); else STATE.collection.delete(cb.value);
    syncAllPanels();
    fireChange();
  });
  delegate(panel, 'change', 'input[type="checkbox"][data-filter="options"]', (e, cb) => {
    if (cb.checked) STATE.options.add(cb.value); else STATE.options.delete(cb.value);
    syncAllPanels();
    fireChange();
  });
  delegate(panel, 'change', 'input[type="checkbox"][data-filter="engine"]', (e, cb) => {
    if (cb.checked) STATE.engine.add(cb.value); else STATE.engine.delete(cb.value);
    syncAllPanels();
    fireChange();
  });
  delegate(panel, 'change', 'input[type="checkbox"][data-filter="seats"]', (e, cb) => {
    if (cb.checked) STATE.seats.add(cb.value); else STATE.seats.delete(cb.value);
    syncAllPanels();
    fireChange();
  });
  delegate(panel, 'change', 'input[type="checkbox"][data-filter="availability"]', (e, cb) => {
    if (cb.checked) STATE.availability.add(cb.value); else STATE.availability.delete(cb.value);
    syncAllPanels();
    fireChange();
  });
  delegate(panel, 'change', 'input[type="checkbox"][data-filter="origin"]', (e, cb) => {
    if (cb.checked) STATE.origin.add(cb.value); else STATE.origin.delete(cb.value);
    syncAllPanels();
    fireChange();
  });
  delegate(panel, 'input', '[data-filter="powerMin"]', (e, input) => {
    STATE.powerMin = input.value === '' ? null : +input.value;
    fireChange();
  });
  delegate(panel, 'input', '[data-filter="powerMax"]', (e, input) => {
    STATE.powerMax = input.value === '' ? null : +input.value;
    fireChange();
  });

  // Km inputs + dual-thumb slider (min thumb ↔ kmMin, max thumb ↔ kmMax)
  delegate(panel, 'input', '[data-filter="kmMin"]', (e, input) => {
    STATE.kmMin = input.value === '' ? null : +input.value;
    syncAllPanels();
    fireChange();
  });
  delegate(panel, 'input', '[data-filter="kmMax"]', (e, input) => {
    STATE.kmMax = input.value === '' ? null : +input.value;
    syncAllPanels();
    fireChange();
  });
  delegate(panel, 'input', '[data-role="km-min-slider"]', (e, sl) => {
    let v = +sl.value;
    const hi = STATE.kmMax ?? KM_MAX_CAP;
    if (v > hi) v = hi;                // clamp: min ≤ max
    STATE.kmMin = v <= 0 ? null : v;   // 0 → no lower bound
    syncAllPanels();
    fireChange();
  });
  delegate(panel, 'input', '[data-role="km-max-slider"]', (e, sl) => {
    let v = +sl.value;
    const lo = STATE.kmMin ?? 0;
    if (v < lo) v = lo;                // clamp: max ≥ min
    STATE.kmMax = v >= KM_MAX_CAP ? null : v;
    syncAllPanels();
    fireChange();
  });
}

// Reset works from any [data-action="reset-filters"] anywhere on the page
function resetAll() {
  STATE.q = '';
  STATE.mode = 'all';
  STATE.brands.clear();
  STATE.models.clear();
  STATE.priceMin = null; STATE.priceMax = null;
  STATE.rateMin = null; STATE.rateMax = null;
  STATE.yearMin = null; STATE.yearMax = null;
  STATE.bodies.clear(); STATE.fuels.clear();
  STATE.trans.clear(); STATE.colors.clear();
  STATE.drives.clear();
  STATE.collection.clear(); STATE.options.clear(); STATE.engine.clear();
  STATE.seats.clear(); STATE.availability.clear(); STATE.origin.clear();
  STATE.powerMin = null; STATE.powerMax = null;
  STATE.cities.clear();
  STATE.kmMin = null; STATE.kmMax = null;
  STATE.consumptionMax = null;
  syncAllPanels();
  fireChange();
}

// Set the BRAND filter to an exact set of slugs (REPLACE, not toggle). Used by
// the catalog's manufacturer quick-filter pills, which show whenever the
// selection isn't a single brand; "Toate" passes [] to clear it. Mirrors the
// brand branch of `multiselect:change` so STATE, the URL, the applied chips and
// the results stay in step.
//
// The pills carry RO slugs while STATE.brands holds canonical brand NAMES, so
// every value goes through canonicalizeBrand() (it accepts either form).
export function setBrandFilter(slugs) {
  const values = Array.isArray(slugs)
    ? slugs.map((s) => String(s)).filter(Boolean)
    : (slugs ? [String(slugs)] : []);
  const names = values.map((s) => canonicalizeBrand(s));
  STATE.brands = new Set(names);
  // An explicit brand supersedes a leftover free-text search - see the
  // `multiselect:change` brand branch for why combining them zeroes results.
  STATE.q = '';
  // Dropping or swapping the brand invalidates models picked under the old one
  // (and clears them entirely when no brand is left).
  pruneOrphanModels();
  // Reflect into the brand multiselect so a pick from the row also ticks the
  // sidebar. Guarded: a no-op before the panel exists.
  panels.forEach((panel) => {
    const brandRoot = qs('[data-filter="brand"][data-component="multiselect"]', panel);
    if (!brandRoot) return;
    const brand = getMultiselect(brandRoot);
    if (brand && typeof brand.setSelected === 'function') brand.setSelected(names);
  });
  syncAllPanels();
  fireChange();
}

// Set the MODEL filter to an exact set of slugs (REPLACE, not toggle). Used by
// the single-brand "models row" quick-filter pills in catalog.js: tapping a
// model replaces the model selection with just that one; "Toate" passes [] to
// clear it. Mirrors the model multiselect's own `multiselect:change` branch so
// STATE, the URL, the applied chips and the results all stay in step.
//
// Works even before that brand's model options have been fetched into the
// multiselect: STATE + URL + REST results are what drive the listing, and the
// applied-chips + the models row itself surface the selection. When the model
// multiselect IS loaded, we reflect the selection into it so the sidebar UI
// matches; syncBrandModelMultis reconciles again once options arrive.
export function setModelFilter(slugs) {
  const values = Array.isArray(slugs)
    ? slugs.map((s) => String(s)).filter(Boolean)
    : (slugs ? [String(slugs)] : []);
  STATE.models = new Set(values);
  // An explicit model supersedes a leftover free-text search (matches the
  // `multiselect:change` model branch), otherwise combining the two can zero out
  // results.
  STATE.q = '';
  // Reflect into the model multiselect UI when its instance already exists, so a
  // pick from the row also ticks the sidebar. Guarded: no-op before the brand's
  // options are loaded (the setSelected below just records the values).
  panels.forEach((panel) => {
    const modelRoot = qs('[data-filter="model"][data-component="multiselect"]', panel);
    if (!modelRoot) return;
    const model = getMultiselect(modelRoot);
    if (model && typeof model.setSelected === 'function') model.setSelected(values);
  });
  syncAllPanels();
  fireChange();
}

// Resolve an availability slug to its human, translated label by reading the
// matching server-rendered checkbox's own label text (so RO/RU stay in sync).
// Falls back to the raw slug if the checkbox isn't in the DOM.
function availabilityLabel(slug) {
  const cb = qs(`input[type="checkbox"][data-filter="availability"][value="${CSS.escape(String(slug))}"]`);
  if (cb) {
    const label = qs('span:not(.ap-checkbox__box):not(.ap-checkbox__count)', cb.closest('.ap-checkbox') || cb.parentElement);
    const text = label && label.textContent ? label.textContent.trim() : '';
    if (text) return text;
  }
  return String(slug);
}

// Applied chips bar (above results).
//
// The server pre-renders these chips into `applied-list` for crawlers/no-JS
// (template-parts/catalog/toolbar.php, same .ap-applied__chip markup, with the
// remove control as an <a href> to the filter-removed URL). On load we RECONCILE
// by overwriting `list.innerHTML` from the client STATE (parsed from the URL),
// which replaces the server markup wholesale - so there's no doubling - and
// swaps the <a> for an index-bound <button> wired to the in-page remove handler.
function renderApplied() {
  const wrap = qs('[data-role="applied-chips"]');
  const list = qs('[data-role="applied-list"]');
  if (!wrap || !list) return;

  const chips = [];
  const add = (label, onRemove) => chips.push({ label, onRemove });

  if (STATE.q) add(`„${STATE.q}"`, () => { STATE.q = ''; });
  STATE.brands.forEach((b) => add(brandDisplay(b), () => {
    STATE.brands.delete(b);
    // Removing a brand removes its models too (and all models if none remain).
    pruneOrphanModels();
  }));
  STATE.models.forEach((m) => add(modelLabel(m), () => STATE.models.delete(m)));
  STATE.cities.forEach((c) => add(facetLabel('dealer_location', c) || c, () => STATE.cities.delete(c)));
  STATE.bodies.forEach((b) => add(bodyLabel(b), () => STATE.bodies.delete(b)));
  STATE.fuels.forEach((f) => add(facetLabel('car_fuel', f) || f, () => STATE.fuels.delete(f)));
  STATE.trans.forEach((t) => add(facetLabel('Transmission', t) || t, () => STATE.trans.delete(t)));
  STATE.colors.forEach((c) => add(facetLabel('Colors', c) || c, () => STATE.colors.delete(c)));
  STATE.drives.forEach((d) => add(facetLabel('Drive', d) || d, () => STATE.drives.delete(d)));
  STATE.collection.forEach((c) => add(facetLabel('collection', c) || c, () => STATE.collection.delete(c)));
  STATE.options.forEach((o) => add(facetLabel('Features', o) || o, () => STATE.options.delete(o)));
  STATE.engine.forEach((en) => add(facetLabel('Engine Size', en) || en, () => STATE.engine.delete(en)));
  STATE.seats.forEach((s) => add(`${s} locuri`, () => STATE.seats.delete(s)));
  STATE.availability.forEach((a) => add(availabilityLabel(a), () => STATE.availability.delete(a)));
  STATE.origin.forEach((o) => add(o, () => STATE.origin.delete(o)));
  if (STATE.priceMin != null || STATE.priceMax != null) {
    const lo = STATE.priceMin != null ? num(STATE.priceMin) : '0';
    const hi = STATE.priceMax != null ? num(STATE.priceMax) : '∞';
    add(`${lo}–${hi} €`, () => { STATE.priceMin = null; STATE.priceMax = null; });
  }
  if (STATE.rateMin != null || STATE.rateMax != null) {
    const lo = STATE.rateMin != null ? num(STATE.rateMin) : '0';
    const hi = STATE.rateMax != null ? num(STATE.rateMax) : '∞';
    add(`${lo}–${hi} ${t('per_month', '€/lună')}`, () => { STATE.rateMin = null; STATE.rateMax = null; });
  }
  if (STATE.powerMin != null || STATE.powerMax != null) {
    add(`${STATE.powerMin ?? '0'}–${STATE.powerMax ?? '∞'} CP`, () => { STATE.powerMin = null; STATE.powerMax = null; });
  }
  if (STATE.yearMin != null || STATE.yearMax != null) {
    add(`${STATE.yearMin ?? '…'}–${STATE.yearMax ?? '…'}`, () => { STATE.yearMin = null; STATE.yearMax = null; });
  }
  if (STATE.kmMin != null || STATE.kmMax != null) {
    const lo = STATE.kmMin != null ? num(STATE.kmMin) : '0';
    const hi = STATE.kmMax != null ? num(STATE.kmMax) : '∞';
    add(`${lo}–${hi} km`, () => { STATE.kmMin = null; STATE.kmMax = null; });
  }
  if (STATE.consumptionMax != null) {
    add(`${t('consumption_le', 'consum ≤')} ${num(STATE.consumptionMax)} l/100 km`, () => { STATE.consumptionMax = null; });
  }

  wrap.hidden = chips.length === 0;
  list.innerHTML = chips.map((c, i) => `
    <span class="ap-applied__chip">${c.label}<button class="ap-applied__chip-x" type="button" data-chip="${i}" aria-label="${t('remove_filter', 'Elimină filtrul')}">×</button></span>
  `).join('');

  qsa('[data-chip]', list).forEach((btn) => {
    btn.addEventListener('click', () => {
      const i = +btn.dataset.chip;
      if (chips[i]) {
        chips[i].onRemove();
        syncAllPanels();
        fireChange();
      }
    });
  });

  // Keep the breadcrumb in sync with the active filters (server renders it only
  // on a full page load; in-page filtering uses pushState). Mirrors the server:
  // the hierarchical Brand [› Model] path - the brand becomes an intermediate
  // LINK when a model is also selected, so it reads Home › Catalog › Audi › A5.
  // Non-hierarchical facets fall back to the first active chip.
  LAST_ACTIVE_FILTERS = chips.length;
  const bcNav = qs('[data-role="breadcrumb"]');
  if (bcNav) {
    const brandSep = qs('[data-role="crumb-brand-sep"]', bcNav);
    const brandEl = qs('[data-role="crumb-brand"]', bcNav);
    const finalSep = qs('[data-role="crumb-final-sep"]', bcNav);
    const finalEl = qs('[data-role="crumb-final"]', bcNav);
    if (brandSep && brandEl && finalSep && finalEl) {
      const brandSlug = STATE.brands.size ? [...STATE.brands][0] : '';
      const modelSlug = STATE.models.size ? [...STATE.models][0] : '';
      const brandLbl = brandSlug ? brandDisplay(brandSlug) : '';
      const modelLbl = modelSlug ? modelLabel(modelSlug) : '';
      const inter = !!(brandLbl && modelLbl); // brand is an intermediate link only alongside a model
      const finalLbl = modelLbl || brandLbl || (chips.length ? String(chips[0].label) : '');
      if (inter) {
        brandEl.textContent = brandLbl;
        const base = (AP_DATA && AP_DATA.catalog ? AP_DATA.catalog : '/catalog/').replace(/\/+$/, '/');
        brandEl.setAttribute('href', base + brandSlug + '/');
      }
      brandEl.hidden = !inter;
      brandSep.hidden = !inter;
      finalEl.textContent = finalLbl;
      finalEl.hidden = !finalLbl;
      finalSep.hidden = !finalLbl;
    }
  }
}

let panels = [];
function syncAllPanels() {
  panels.forEach(syncPanelToState);
  syncModeTabs();
}

export function initFilterPanel() {
  // Pretty URL (/catalog/renault/model-juke/) wins over hash/query because
  // it's the canonical state from the server.
  readFromPath();
  readFromHash();
  readFromQuery();

  const inline = qs('.ap-filter-panel--inline');
  const drawer = qs('#ap-filter-drawer');
  const drawerBody = drawer ? qs('.ap-filter-drawer__body', drawer) : null;

  if (inline && drawerBody) {
    drawerBody.innerHTML = inline.outerHTML.replace('ap-filter-panel--inline', 'ap-filter-panel--drawer');
    // Cloned multiselects come over with `data-bound="1"` from the source.
    // Strip it so initMultiselects creates fresh instances bound to the new
    // DOM elements (the WeakMap key is the element, not the markup).
    qsa('[data-component="multiselect"]', drawerBody).forEach((el) => { delete el.dataset.bound; });
  }

  panels = qsa('[data-component="filter-panel"]');
  // Attach multiselects in both inline and cloned drawer copies before binding
  panels.forEach((p) => initMultiselects(p));
  panels.forEach((p) => { bindPanel(p); syncPanelToState(p); });

  // Reset is a singular action - bind once on document so it works from
  // anywhere ("Resetează"/"Șterge tot" buttons in filter panel, drawer, chip bar).
  delegate(document, 'click', '[data-action="reset-filters"]', (e) => {
    e.preventDefault();
    resetAll();
  });

  // Mode tabs (All/Used/New) live above the catalog title bar - bind at document scope
  delegate(document, 'click', '[data-mode]', (e, btn) => {
    e.preventDefault();
    STATE.mode = btn.dataset.mode;
    syncAllPanels();
    fireChange();
  });
  syncModeTabs();

  if (drawer) {
    delegate(document, 'click', '[data-action="open-filter-drawer"]', (e) => {
      e.preventDefault();
      if (typeof drawer.showModal === 'function') drawer.showModal();
      else drawer.setAttribute('open', '');
      lockScroll(true);
    });
    const close = () => {
      if (typeof drawer.close === 'function') drawer.close();
      else drawer.removeAttribute('open');
      lockScroll(false);
    };
    delegate(drawer, 'click', '[data-action="close-filter-drawer"]', close);
    delegate(drawer, 'click', '[data-action="apply-filters"]', close);
    on(drawer, 'click', (e) => { if (e.target === drawer) close(); });
    on(drawer, 'cancel', (e) => { e.preventDefault(); close(); });
    on(drawer, 'close', () => lockScroll(false));
  }

  fireChange();
}
